import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BillOfLadingDetail,
  CompanyAggregatedByBOL,
  PortOriginTransactionProportions,
  SupplierFromBuyers,
  TradeDataBOLs,
  TradeDataEntityDetail,
  TradeDataListTypeAIExplanation,
  TradeDataMarketAnalytics,
  TradeDataMarketAnalyticsAIExplanation,
  TradeDataMarketAnalyticsCompany,
  TradeDataMarketAnalyticsWorldMap,
  TradeDataPort,
  TradeDataProduct,
  TradeDataProductDetail,
} from 'src/model/tradeData';
import { TradeDataService } from 'src/services/api';

import { map } from 'lodash';
import { CommonResponse, CommonResponseList } from 'src/model/common';
import { TRADE_DATA_CACHE_TIME, TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME } from 'src/utils/constants/config';
import { TypeBuyerSupplier } from 'src/utils/constants/enum';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { FieldRenameMapper } from 'src/utils/helpers/dataMappers/dataMappers';
import { handleBadRecordName } from 'src/utils/helpers/function/handleBadRecordName';
import {
  IGetListTradeDataPortPayload,
  IGetSupplierBuyerCountryRequestPayload,
  IGetTradeDataHSCodeDetail,
  TypeQueryBuyersProportion,
  TypeQueryParamPortTransaction,
} from 'src/utils/typings/dto/tradeData';

const QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE = 'QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE';
const QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE_ONLY_HS6 = 'QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE_ONLY_HS6';
const QUERY_KEY_GET_TRADE_DATA_HS_CODE_DETAIL = 'QUERY_KEY_GET_TRADE_DATA_HS_CODE_DETAIL';
const QUERY_KEY_LIST_TRADE_DATA_PORT = 'QUERY_KEY_LIST_TRADE_DATA_PORT';
const QUERY_KEY_GET_TRADE_DATA_PORT_DETAIL = 'QUERY_KEY_GET_TRADE_DATA_PORT_DETAIL';

const QUERY_KEY_GET_TRADE_DATA_ENTITY_DETAIL = 'QUERY_KEY_GET_TRADE_DATA_ENTITY_DETAIL';
const QUERY_KEY_LIST_BILL_OF_LADING = 'QUERY_KEY_LIST_BILL_OF_LADING';
const QUERY_KEY_BILL_OF_LADING_DETAIL = 'QUERY_KEY_BILL_OF_LADING_DETAIL';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_CHART = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_CHART';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_AI_EXPLANATION = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_AI_EXPLANATION';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_TABLE = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_TABLE';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_COMPANY = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_COMPANY';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_BUYERS = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_BUYERS';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_SUPPLIERS = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_SUPPLIERS';
const QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_WORLD_MAP = 'QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_WORLD_MAP';
export const QUERY_KEY_PORT_TRANSACTION_PROPORTION = 'QUERY_KEY_PORT_TRANSACTION_PROPORTION';
export const QUERY_KEY_PORT_OF_ORIGIN = 'QUERY_KEY_PORT_OF_ORIGIN';
export const QUERY_KEY_LIST_BUYERS_SUPPLIER_BY_PORT = 'QUERY_KEY_LIST_BUYER_SUPPLIER_BY_PORT';
export const QUERY_KEY_LIST_BUYERS_BY_PORT = 'QUERY_KEY_LIST_BUYERS_BY_PORT';
export const QUERY_KEY_LIST_SUPPLIERS_BY_PORT = 'QUERY_KEY_LIST_SUPPLIERS_BY_PORT';
export const QUERY_KEY_LIST_BUYERS_PROPORTION = 'QUERY_KEY_LIST_BUYERS_PROPORTION';
export const QUERY_KEY_LIST_BUYERS_DETAIL = 'QUERY_KEY_LIST_BUYERS_DETAIL';
export const QUERY_KEY_LIST_PARTNERS_FROM_COMPANY = 'QUERY_KEY_LIST_PARTNERS_FROM_COMPANY';

export const QUERY_KEY_LIST_BUYER_SUPPLIER_BY_PORT = 'QUERY_KEY_LIST_BUYER_SUPPLIER_BY_PORT';

const QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_WORLD_MAP = 'QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_WORLD_MAP';
const QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_AI_EXPLANATION =
  'QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_AI_EXPLANATION';
export const QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_TABLE = 'QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_TABLE';
export const QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_COMPANY =
  'QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_COMPANY';

export const QUERY_KEY_GET_TRADING_COMPANIES = 'QUERY_KEY_GET_TRADING_COMPANIES';

const QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT = 'QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT';
const QUERY_KEY_TRADE_DATA_TRANSACTIONS_PAYMENT_STATUS = 'QUERY_KEY_TRADE_DATA_TRANSACTIONS_PAYMENT_STATUS';
const QUERY_KEY_UNIT_PRICE = 'QUERY_KEY_UNIT_PRICE';
const QUERY_KEY_UNIT_PRICE_CHART = 'QUERY_KEY_UNIT_PRICE_CHART';
const QUERY_KEY_AVERAGE_UNIT_PRICE_CHART = 'QUERY_KEY_AVERAGE_UNIT_PRICE_CHART';
const QUERY_KEY_AVERAGE_UNIT_PRICE_TABLE = 'QUERY_KEY_AVERAGE_UNIT_PRICE_TABLE';

interface IGetListTradeDataHSCodePayload {
  country_code?: string;
  keyword?: string;
  offset?: number;
  limit?: number;
}

export const useQueryListTradeDataHSCode = (query: IGetListTradeDataHSCodePayload) => {
  const queryKey = QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE;

  const serviceFn = TradeDataService.getListTradeDataHSCode;
  const { data, isFetching, refetch } = useQuery({
    queryKey: [queryKey, query],
    queryFn: () => serviceFn(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataProduct(),
    enabled: !!query.keyword,
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListTradeDataPort = (query: IGetListTradeDataPortPayload) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_TRADE_DATA_PORT, query],
    queryFn: () => TradeDataService.getListPort(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataPort(),
    enabled: !!query.country_code,
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryTradeDataPortDetail = (query: IGetListTradeDataPortPayload) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_GET_TRADE_DATA_PORT_DETAIL, query],
    queryFn: () => TradeDataService.getListPort(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataPort(),
    enabled: !!query.country_code && !!query.keyword,
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListTradeDataEntityDetail = (id: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_GET_TRADE_DATA_ENTITY_DETAIL, id],
    queryFn: () => TradeDataService.getEntityDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataEntityDetail(),
    enabled: !!id,
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataHSCodeDetail = (query: IGetTradeDataHSCodeDetail) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_GET_TRADE_DATA_HS_CODE_DETAIL, query],
    queryFn: () => TradeDataService.getHSCodeDetail(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataProductDetail(),
    placeholderData: (previousData) => previousData,
    enabled: !!query.code,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListBOLs = (query: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_BILL_OF_LADING, query],
    queryFn: () => TradeDataService.getListBOLs(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataBOLs(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  const dataMapping = getArrayDefault(data?.data).map((item) => ({
    ...item,
    transaction: {
      ...item?.transaction,
      bol: handleBadRecordName(item?.transaction?.bol) || null,
    },
  }));

  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryBillOfLadingDetail = (id: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_BILL_OF_LADING_DETAIL, id],
    queryFn: () => TradeDataService.getBillOfLadingDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new BillOfLadingDetail(),
    placeholderData: (previousData) => previousData,
  });

  const mappedData = {
    ...data?.data,
  };
  if (mappedData?.transaction?.bol) {
    mappedData.transaction.bol = handleBadRecordName(data?.data?.transaction?.bol) || null;
  }
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data: {
      ...data,
      data: mappedData,
    },
    isError,
    isPending,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticsChart = (query: any) => {
  const { data, isFetching, isFetched, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_CHART, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticsChart(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalytics(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, IMarketAnalytics>(item, {
      date: 'month',
      number_of_bol: 'bol',
      value: 'value_usd',
    }),
  );

  return {
    data,
    dataMapping,
    isFetched,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticsAIExplanation = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_AI_EXPLANATION, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticsAIExplanation(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsAIExplanation(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticsTable = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_TABLE, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticsTable(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalytics(),
    placeholderData: (previousData) => previousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!query,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, IMarketAnalytics>(item, {
      date: 'month',
      number_of_bol: 'bol',
    }),
  );

  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticBuyers = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_BUYERS, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticBuyers(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsCompany(),
    enabled: !!query,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticSuppliers = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTIC_SUPPLIERS, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticSuppliers(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsCompany(),
    enabled: !!query,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradeDataMarketAnalyticsWorldMap = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_MARKET_ANALYTICS_WORLD_MAP, query],
    queryFn: () => TradeDataService.getTradeDataMarketAnalyticsWorldMap(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsWorldMap(),
    placeholderData: (previousData) => previousData,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!query,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, IMarketAnalyticsWorldMap>(item, {
      number_of_bol: 'bol',
      value: 'value_usd',
      value_usd: 'value_usd',
    }),
  );

  return {
    data,
    dataMapping,
    isFetching,
    refetch,
  };
};

export const useQueryPortTransactionProportion = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_PORT_TRANSACTION_PROPORTION, params],
    queryFn: () => TradeDataService.getPortTransactionProportion(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PortOriginTransactionProportions(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!params,
  });

  return {
    data,
    isFetching,
  };
};
export const useQueryPortOrigin = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_PORT_OF_ORIGIN, params],
    queryFn: () => TradeDataService.getListPortOrigin(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!params,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const dataMapping = map(
    data.data,
    ({
      port_id,
      port_name,
      country_code,
      bol,
      weight,
      quantity,
      buyers,
      suppliers,
      port_code,
      value_usd,
      port_locode,
    }) =>
      ({
        id: port_id,
        port_name: port_name,
        country_code: country_code,
        shipments: bol,
        weight: weight,
        teu: quantity,
        value_usd: value_usd,
        total_buyers: buyers,
        total_suppliers: suppliers,
        port_code: port_code,
        port_locode: port_locode,
      }) as ITradeDataByProportionalPort,
  );

  return {
    data,
    isFetching,
    dataMapping,
  };
};

export const useQueryBuyersProportion = (params?: TypeQueryBuyersProportion) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_BUYERS_PROPORTION, params],
    queryFn: () => TradeDataService.getBuyersAndSupplierProportion(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!params,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryBuyersAndSuppliersDetail = (params?: TypeQueryBuyersProportion) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_BUYERS_DETAIL, params],
    queryFn: () => TradeDataService.getBuyersAndSuppliersDetail(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CompanyAggregatedByBOL(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!params,
  });

  return {
    data,
    isFetching,
  };
};

export const useQueryListPartnersFromCompany = (
  params: TypeQueryBuyersProportion,
  companyId: string,
  partnerType: TypeBuyerSupplier,
) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_PARTNERS_FROM_COMPANY, params, companyId],
    queryFn: () => TradeDataService.getListPartnersFromCompany(params, companyId || '', partnerType),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new SupplierFromBuyers(),
    placeholderData: keepPreviousData,
    enabled: Boolean(companyId),
  });

  const isBuyerPartners = partnerType === TypeBuyerSupplier.buyer;

  const dataMapping = map(
    data?.data,
    ({
      bol,
      weight,
      quantity,
      value_usd,

      company_id,
      company_name,
      company_country_code,
    }) =>
      ({
        id: company_id,
        company_name: company_name,
        country_code: company_country_code,
        shipments: bol,
        weight: weight,
        teu: quantity,
        value_usd: value_usd,
      }) as IBuyerInfo,
  );

  return {
    data,
    isFetching,
    dataMapping,
  };
};

export const useQueryListBuyerByPort = ({
  params,
  port_locode = '',
}: {
  params: TypeQueryParamPortTransaction;
  port_locode?: string;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_BUYERS_BY_PORT, params, port_locode],
    queryFn: () => TradeDataService.getListBuyerByPort(params, port_locode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    enabled: Boolean(port_locode),
  });

  const dataMapping = map(
    data.data,
    ({ bol, weight, quantity, value_usd, company_id, company_name, company_country_code }) =>
      ({
        id: company_id,
        company_name: company_name,
        country_code: company_country_code,
        shipments: bol,
        weight: weight,
        teu: quantity,
        value_usd: value_usd,
      }) as IBuyerInfo,
  );
  return { data, isFetching, dataMapping };
};

export const useQueryListSupplierByPort = ({
  params,
  port_locode = '',
}: {
  params: TypeQueryParamPortTransaction;
  port_locode?: string;
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_LIST_SUPPLIERS_BY_PORT, params, port_locode],
    queryFn: () => TradeDataService.getListSupplierByPort(params, port_locode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    enabled: Boolean(port_locode),
  });

  const dataMapping = map(
    data.data,
    ({ bol, weight, quantity, value_usd, company_id, company_name, company_country_code }) =>
      ({
        id: company_id,
        company_name: company_name,
        country_code: company_country_code,
        shipments: bol,
        weight: weight,
        teu: quantity,
        value_usd: value_usd,
      }) as IBuyerInfo,
  );
  return { data, isFetching, dataMapping };
};

export const useQueryGetBuyerSupplierCountryWorldMap = (query?: any) => {
  const { data, isFetching, refetch, isFetched } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_WORLD_MAP, query],
    queryFn: () => TradeDataService.getSupplierBuyerCountryWorldMap(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsWorldMap(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, IMarketAnalyticsWorldMap>(item, {
      number_of_bol: 'bol',
      value: 'value_usd',
    }),
  );

  return {
    data,
    dataMapping,
    isFetching,
    isFetched,
    refetch,
  };
};

export const useQueryGetSupplierCountryAIExplanation = (query?: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_AI_EXPLANATION, query],
    queryFn: () => TradeDataService.getSupplierBuyerCountryAIExplanation(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataListTypeAIExplanation(),
    placeholderData: (previousData) => previousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!query,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetBuyerSupplierCountryTable = (params?: IGetSupplierBuyerCountryRequestPayload) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_TABLE, params],
    queryFn: () => TradeDataService.getSupplierBuyerCountryTable(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
    enabled: !!params,
  });

  const dataMapping = map(data?.data, (item) =>
    FieldRenameMapper.rename<any, ISupplierBuyerCountryTableItem>(item, {
      id: 'country_code',
      country_code: 'country_code',
      shipments: 'bol',
      weight: 'weight',
      teu: 'quantity',
      total_company: 'company',
    }),
  );

  return {
    data,
    dataMapping,
    isFetching,
  };
};

export const useQueryGetBuyerSupplierCountryCompany = (query?: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_SUPPLIER_BUYER_COUNTRY_COMPANY, query],
    queryFn: () => TradeDataService.getSupplierBuyerCountryCompany(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataMarketAnalyticsCompany(),
    enabled: !!query,
    placeholderData: (previousData) => previousData,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryTransactionsCount = (query?: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_TRANSACTIONS_COUNT, query],
    queryFn: () => TradeDataService.getTransactionsCount(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: !!query?.hs_code || !!query?.keyword,
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryTransactionsPaymentStatus = (query?: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_TRANSACTIONS_PAYMENT_STATUS, query],
    queryFn: () => TradeDataService.getTransactionsPaymentStatus(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: !!query?.hs_code || !!query?.keyword,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryCTDTransactionsPaymentStatus = (query: any) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_TRADE_DATA_TRANSACTIONS_PAYMENT_STATUS, query],
    queryFn: () => TradeDataService.getTransactionsPaymentStatus(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    enabled: !!query?.hs_code,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryGetTradingCompanies = (query: any) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_TRADING_COMPANIES, query],
    queryFn: () => TradeDataService.getTradingCompanies(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!query.keyword || (!!query.keyword && !!query.country_code),
    initialData: new CommonResponseList(),
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryListHSCodeAIReport = (query: IGetListTradeDataHSCodePayload) => {
  const queryKey = QUERY_KEY_SEARCH_LIST_TRADE_DATA_HS_CODE;

  const serviceFn = TradeDataService.getListHSCodeAIReport;
  const { data, isFetching, refetch } = useQuery({
    queryKey: [queryKey, query],
    queryFn: () => serviceFn(query),
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TradeDataProduct(),
    enabled: !!query.keyword,
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQuerySupplierBuyerUnitPriceTable = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_UNIT_PRICE, params],
    queryFn: () => TradeDataService.getUnitPriceTableBuyersSuppliers(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!params,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const dataMapping = map(
    data.data,
    ({
      entity_id,
      entity_name,
      avg_unit_price,
      total_volume,
      transactions,
      last_update,
      variance_pct,
      status,
      country_code,
    }) =>
      ({
        entity_id: entity_id,
        entity_name: entity_name,
        avg_unit_price: avg_unit_price,
        total_volume: total_volume,
        transactions: transactions,
        last_update: last_update,
        variance_pct: variance_pct,
        status: status,
        country_code: country_code,
      }) as IBuyerSupplierUnitPrice,
  );

  return {
    data,
    dataMapping,
    isFetching,
  };
};

export const useQuerySupplierBuyerUnitPriceChart = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_UNIT_PRICE_CHART, params],
    queryFn: () => TradeDataService.getUnitPriceChartBuyersSuppliers(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponse(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!params,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const dataMapping = map(
    data?.data,
    ({ entity_id, entity_name, avg_unit_price, total_measure, is_significantly_high, country_code }) =>
      ({
        entity_id: entity_id,
        entity_name: entity_name,
        avg_unit_price: avg_unit_price,
        total_volume: total_measure,
        is_significantly_high: is_significantly_high,
        country_code: country_code,
      }) as IBuyerSupplierUnitPrice,
  );

  return {
    data,
    dataMapping,
    isFetching,
  };
};

export const useQueryAverageUnitPriceChart = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_AVERAGE_UNIT_PRICE_CHART, params],
    queryFn: () => TradeDataService.getAverageUnitPriceChart(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponse(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!params,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const dataMapping = map(
    data?.data,
    ({ avg_unit_price, total_measure, is_significantly_high, month }) =>
      ({
        avg_unit_price: avg_unit_price,
        total_volume: total_measure,
        is_significantly_high: is_significantly_high,
        month: month,
      }) as IBuyerSupplierUnitPriceMonthly,
  );

  return {
    data,
    dataMapping,
    isFetching,
  };
};

export const useQueryAverageUnitPriceTable = (params?: TypeQueryParamPortTransaction) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_AVERAGE_UNIT_PRICE_TABLE, params],
    queryFn: () => TradeDataService.getAverageUnitPriceTable(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
    staleTime: TRADE_DATA_CACHE_TIME,
    enabled: !!params,
    initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  const dataMapping = map(
    data.data,
    ({ month, avg_unit_price, total_volume, transactions, variance_pct, status }) =>
      ({
        month: month,
        avg_unit_price: avg_unit_price,
        total_volume: total_volume,
        transactions: transactions,
        variance_pct: variance_pct,
        status: status,
      }) as IBuyerSupplierUnitPriceMonthly,
  );

  return {
    data,
    dataMapping,
    isFetching,
  };
};
